/**
 * DOM Utilities
 */

/**
 * Escape HTML special characters
 */
export function escapeHtml(str: string): string {
  const div = document.createElement('div');
  div.textContent = str;
  return div.innerHTML;
}

/**
 * Sanitize HTML - allow safe formatting tags, strip scripts and event handlers.
 * Used for rendering rich-text product descriptions.
 */
const ALLOWED_TAGS = new Set([
  'p', 'br', 'b', 'i', 'em', 'strong', 'u', 's', 'sub', 'sup',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'ul', 'ol', 'li',
  'a', 'span', 'div', 'blockquote', 'pre', 'code',
  'table', 'thead', 'tbody', 'tr', 'th', 'td',
  'img', 'hr',
]);

const ALLOWED_ATTRS = new Set([
  'href', 'target', 'rel', 'src', 'alt', 'width', 'height',
  'class', 'style',
]);

export function sanitizeHtml(html: string): string {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  sanitizeNode(doc.body);
  return doc.body.innerHTML;
}

function sanitizeNode(node: Node): void {
  const children = Array.from(node.childNodes);
  for (const child of children) {
    if (child.nodeType === Node.ELEMENT_NODE) {
      const el = child as Element;
      const tag = el.tagName.toLowerCase();

      if (!ALLOWED_TAGS.has(tag)) {
        // Replace disallowed element with its text content
        const text = document.createTextNode(el.textContent || '');
        node.replaceChild(text, child);
        continue;
      }

      // Strip disallowed attributes (including on* event handlers)
      const attrs = Array.from(el.attributes);
      for (const attr of attrs) {
        if (!ALLOWED_ATTRS.has(attr.name) || attr.name.startsWith('on')) {
          el.removeAttribute(attr.name);
        }
      }

      // Force external links to open safely
      if (tag === 'a') {
        el.setAttribute('target', '_blank');
        el.setAttribute('rel', 'noopener noreferrer');
      }

      sanitizeNode(el);
    }
  }
}

/**
 * Create element with attributes and children
 */
export function createElement<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs?: Record<string, string>,
  children?: (Node | string)[]
): HTMLElementTagNameMap[K] {
  const el = document.createElement(tag);

  if (attrs) {
    Object.entries(attrs).forEach(([key, value]) => {
      if (key === 'className') {
        el.className = value;
      } else if (key.startsWith('data-')) {
        el.setAttribute(key, value);
      } else {
        (el as any)[key] = value;
      }
    });
  }

  if (children) {
    children.forEach((child) => {
      if (typeof child === 'string') {
        el.appendChild(document.createTextNode(child));
      } else {
        el.appendChild(child);
      }
    });
  }

  return el;
}

/**
 * Query selector with type assertion
 */
export function $(
  selector: string,
  parent: ParentNode = document
): HTMLElement | null {
  return parent.querySelector(selector);
}

/**
 * Query selector all with type assertion
 */
export function $$(
  selector: string,
  parent: ParentNode = document
): HTMLElement[] {
  return Array.from(parent.querySelectorAll(selector));
}

/**
 * Wait for an element to exist in DOM
 */
export function waitForElement(
  selector: string,
  parent: ParentNode = document,
  timeout: number = 5000
): Promise<HTMLElement> {
  return new Promise((resolve, reject) => {
    const element = $(selector, parent);
    if (element) {
      resolve(element);
      return;
    }

    const observer = new MutationObserver(() => {
      const element = $(selector, parent);
      if (element) {
        observer.disconnect();
        resolve(element);
      }
    });

    observer.observe(parent as Node, {
      childList: true,
      subtree: true,
    });

    setTimeout(() => {
      observer.disconnect();
      reject(new Error(`Element ${selector} not found within ${timeout}ms`));
    }, timeout);
  });
}

/**
 * Get container element from string selector or element
 */
export function getContainer(
  container: string | HTMLElement | undefined
): HTMLElement | null {
  if (!container) return null;
  if (typeof container === 'string') {
    return $(container);
  }
  return container;
}

/**
 * Create a loading spinner element
 */
export function createSpinner(size: 'sm' | 'md' | 'lg' = 'md'): HTMLElement {
  const sizeMap = { sm: '16px', md: '24px', lg: '32px' };
  const spinner = createElement('div', {
    className: 'ks-spinner',
  });
  spinner.style.width = sizeMap[size];
  spinner.style.height = sizeMap[size];
  return spinner;
}

/**
 * Create error message element
 */
export function createError(message: string): HTMLElement {
  return createElement('div', { className: 'ks-error' }, [message]);
}

/**
 * Debounce function calls
 */
export function debounce<T extends (...args: any[]) => any>(
  fn: T,
  delay: number
): (...args: Parameters<T>) => void {
  let timeoutId: ReturnType<typeof setTimeout>;

  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), delay);
  };
}

/**
 * Throttle function calls
 */
export function throttle<T extends (...args: any[]) => any>(
  fn: T,
  limit: number
): (...args: Parameters<T>) => void {
  let lastCall = 0;

  return (...args: Parameters<T>) => {
    const now = Date.now();
    if (now - lastCall >= limit) {
      lastCall = now;
      fn(...args);
    }
  };
}

/**
 * Check if element is in viewport
 */
export function isInViewport(element: HTMLElement): boolean {
  const rect = element.getBoundingClientRect();
  return (
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
    rect.right <= (window.innerWidth || document.documentElement.clientWidth)
  );
}

/**
 * Lazy load images when they enter viewport
 */
export function lazyLoadImages(container: ParentNode): void {
  if (!('IntersectionObserver' in window)) return;

  const images = $$('img[data-src]', container);
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const img = entry.target as HTMLImageElement;
          const src = img.dataset.src;
          if (src) {
            img.src = src;
            img.removeAttribute('data-src');
          }
          observer.unobserve(img);
        }
      });
    },
    { rootMargin: '100px' }
  );

  images.forEach((img) => observer.observe(img));
}
